/**
 * goalOutcomeCopy — pure copy resolver for the Goal Outcome Loop
 * end-of-lesson card.
 *
 * Stage-2 P6 (Feb 18, 2026). Mirrors the backend engine branch
 * order so the UI can render an instant client-side preview while
 * the user is still ticking the four questions. The authoritative
 * outcome is the server's response from /api/goal-outcome/submit;
 * this is for *preview only*.
 *
 * Branch rules (locked, deterministic):
 *   • strengthen_again=true                                → not_yet
 *   • was_a_win=false AND four_times=false                 → not_yet
 *   • four_times=true AND was_a_win=true
 *     AND ready_to_step_up=true                            → yes
 *   • any positive but not all three                       → partly
 *   • all answers undefined                                → partly
 */
export const QUESTIONS = Object.freeze([
  { key: "four_times",       text: "Did you do it successfully four times?" },
  { key: "was_a_win",        text: "Was today a win?" },
  { key: "ready_to_step_up", text: "Are we ready to step up next lesson?" },
  { key: "strengthen_again", text: "Or do we strengthen this again first?" },
]);


export const HEADLINES = Object.freeze({
  yes:     "Today felt like a win. Ready to step up next time.",
  partly:  "Today gave us mixed signal. We can keep building on this.",
  not_yet: "Today gave us useful information.",
});


export const NEXT_STEP_LABELS = Object.freeze({
  step_up:       "Step up next lesson",
  strengthen:    "Strengthen this again",
  calmer_repeat: "Try a calmer repetition first",
});


export const OUTCOME_LABELS = Object.freeze({
  yes:     "Yes",
  partly:  "Partly",
  not_yet: "Not yet",
});


function asBool(v) {
  if (v === true)  return true;
  if (v === false) return false;
  return undefined;
}


export function resolveOutcome(answers) {
  const a = answers || {};
  const four_times       = asBool(a.four_times);
  const was_a_win        = asBool(a.was_a_win);
  const ready_to_step_up = asBool(a.ready_to_step_up);
  const strengthen_again = asBool(a.strengthen_again);

  if (strengthen_again === true) {
    return _envelope("not_yet", "calmer_repeat");
  }
  if (was_a_win === false && four_times === false) {
    return _envelope("not_yet", "calmer_repeat");
  }
  if (four_times === true && was_a_win === true && ready_to_step_up === true) {
    return _envelope("yes", "step_up");
  }
  const anyPositive = [four_times, was_a_win, ready_to_step_up]
    .some((x) => x === true);
  if (anyPositive) return _envelope("partly", "strengthen");
  return _envelope("partly", "strengthen");
}


function _envelope(outcome, intent) {
  return {
    outcome,
    outcome_label:    OUTCOME_LABELS[outcome],
    headline:         HEADLINES[outcome],
    next_step_intent: intent,
    next_step_label:  NEXT_STEP_LABELS[intent],
  };
}
